import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import {
  CHANNEL_LABELS,
  FULFILMENT_LABELS,
  isAggregatorChannel,
  type FulfilmentStatus,
  type OrderChannel,
} from '@kortex/shared';
import { useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { money, money0 } from '../lib/format';
import { PageHeader } from '../components/Layout';
import {
  Drawer,
  Empty,
  ErrorState,
  Kpi,
  Loading,
  Modal,
  Select,
  TextInput,
  useToast,
} from '../components/ui';

interface OnlineOrder {
  id: string;
  number: string;
  channel: OrderChannel;
  fulfilment: FulfilmentStatus;
  externalRef: string | null;
  customerName: string | null;
  customerPhone: string | null;
  deliveryAddress: string | null;
  tableCode: string | null;
  commissionPct: number;
  placedAt: string;
  nextStatuses: FulfilmentStatus[];
  itemCount: number;
  lines: Array<{ id: string; name: string; quantity: number; note: string | null }>;
  invoiceId: string | null;
  invoiceNumber: string | null;
  totals: { subtotal: number; total: number };
  payout: { commission: number; commissionGst: number; payout: number };
  events: Array<{ id: string; status: FulfilmentStatus; note: string | null; at: string }>;
}

interface MenuRow {
  id: string;
  name: string;
  price: number;
  isActive: boolean;
  category: string;
}

/** Live columns, in the order an order actually moves through them. */
const BOARD: FulfilmentStatus[] = ['NEW', 'ACCEPTED', 'PREPARING', 'READY', 'DISPATCHED'];

const minutesSince = (iso: string): number =>
  Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60_000));

const channelTone = (channel: OrderChannel): string =>
  channel === 'SWIGGY' ? 'warn' : channel === 'ZOMATO' ? 'danger' : channel === 'WEB' ? 'ok' : '';

/**
 * The board a restaurant works off during service.
 *
 * Orders arrive from an aggregator, the phone or the hotel's own QR menu and
 * move left to right. The clock on each card is the thing that matters: an
 * aggregator counts late orders against you, and a diner watching a rider app
 * notices long before anybody in the kitchen does.
 */
export function OnlineOrdersPage() {
  const queryClient = useQueryClient();
  const { can } = useAuth();
  const { push, fail } = useToast();
  const [channel, setChannel] = useState<'ALL' | OrderChannel>('ALL');
  /*
   * A notification can name the thing it is about, so clicking one opens it
   * rather than dropping somebody on a board to find it again themselves.
   */
  const [params, setParams] = useSearchParams();
  const [openId, setOpenId] = useState<string | null>(params.get('open'));

  useEffect(() => {
    const wanted = params.get('open');
    if (wanted) setOpenId(wanted);
  }, [params]);

  const closeDrawer = () => {
    setOpenId(null);
    if (params.get('open')) {
      const next = new URLSearchParams(params);
      next.delete('open');
      setParams(next, { replace: true });
    }
  };
  const [taking, setTaking] = useState(false);
  const [paying, setPaying] = useState<OnlineOrder | null>(null);

  const orders = useQuery({
    queryKey: ['online-orders'],
    queryFn: () => api.get<OnlineOrder[]>('/api/online-orders'),
    // Service is a moving thing; a stale board is a wrong board.
    refetchInterval: 15_000,
  });

  const advance = useMutation({
    mutationFn: ({
      id,
      status,
      paymentMode,
    }: {
      id: string;
      status: FulfilmentStatus;
      paymentMode?: string;
    }) => api.post(`/api/online-orders/${id}/status`, { status, paymentMode }),
    onSuccess: (_r, { status }) => {
      push(FULFILMENT_LABELS[status], 'ok');
      void queryClient.invalidateQueries({ queryKey: ['online-orders'] });
    },
    onError: fail,
  });

  const billNow = useMutation({
    mutationFn: (id: string) => api.post(`/api/online-orders/${id}/bill`, {}),
    onSuccess: () => {
      push('Billed — it counts towards revenue now', 'ok');
      void queryClient.invalidateQueries({ queryKey: ['online-orders'] });
    },
    onError: fail,
  });

  const rows = useMemo(
    () => (orders.data ?? []).filter((o) => channel === 'ALL' || o.channel === channel),
    [orders.data, channel],
  );

  if (orders.isLoading) return <Loading label="Reading the board…" />;
  if (orders.error)
    return <ErrorState error={orders.error} onRetry={() => void orders.refetch()} />;

  const live = rows.filter((o) => BOARD.includes(o.fulfilment));
  const delivered = rows.filter((o) => o.fulfilment === 'DELIVERED');
  const lost = rows.filter((o) => o.fulfilment === 'CANCELLED' || o.fulfilment === 'REJECTED');
  const unbilled = delivered.filter((o) => !o.invoiceNumber);
  const commission = delivered.reduce(
    (sum, o) => sum + o.payout.commission + o.payout.commissionGst,
    0,
  );
  const sales = delivered.reduce((sum, o) => sum + o.totals.total, 0);

  return (
    <div className="stack">
      <PageHeader
        title="Online Orders"
        subtitle="Swiggy, Zomato, phone and your own QR menu — in one queue"
        actions={
          can('pos:operate') ? (
            <button className="btn primary" onClick={() => setTaking(true)}>
              + Take an order
            </button>
          ) : null
        }
      />

      <div className="kpis">
        <Kpi
          label="Live now"
          value={live.length}
          foot="Not yet delivered"
          tone={live.length ? 'blue' : ''}
        />
        <Kpi label="Delivered" value={delivered.length} foot={money0(sales)} />
        <Kpi
          label="Commission"
          value={money0(commission)}
          foot="On delivered aggregator orders"
          tone={commission > 0 ? 'amber' : ''}
        />
        <Kpi
          label="Cancelled or rejected"
          value={lost.length}
          foot="Nothing was billed"
          tone={lost.length ? 'red' : ''}
        />
      </div>

      <div className="card">
        <div className="card-head">
          <div className="tabs">
            {(['ALL', 'SWIGGY', 'ZOMATO', 'PHONE', 'WEB'] as const).map((id) => (
              <button
                key={id}
                className={`tab ${channel === id ? 'active' : ''}`}
                onClick={() => setChannel(id)}
              >
                {id === 'ALL' ? 'All channels' : CHANNEL_LABELS[id]}
              </button>
            ))}
          </div>
          <span className="small muted">Refreshes every 15 seconds</span>
        </div>

        <div className="card-body">
          {live.length === 0 ? (
            <Empty message="Nothing live right now." />
          ) : (
            <div className="order-board">
              {BOARD.map((status) => {
                const column = live.filter((o) => o.fulfilment === status);
                return (
                  <section key={status} className="order-col">
                    <header>
                      <b>{FULFILMENT_LABELS[status]}</b>
                      <span className="chip">{column.length}</span>
                    </header>
                    <div className="order-col-body">
                      {column.length === 0 ? (
                        <p className="small muted center">—</p>
                      ) : (
                        column.map((order) => {
                          const age = minutesSince(order.placedAt);
                          return (
                            <article
                              key={order.id}
                              className={`order-card ${age > 30 ? 'late' : ''}`}
                              onClick={() => setOpenId(order.id)}
                            >
                              <div className="row between">
                                <span className={`chip ${channelTone(order.channel)}`}>
                                  {CHANNEL_LABELS[order.channel]}
                                </span>
                                <span className={`xs ${age > 30 ? 'warn b' : 'muted'}`}>
                                  {age}m
                                </span>
                              </div>
                              <div className="b">{order.customerName ?? 'Guest'}</div>
                              <div className="xs muted mono">
                                {order.externalRef ?? order.number}
                                {order.tableCode ? ` · Table ${order.tableCode}` : ''}
                              </div>
                              <div className="row between" style={{ marginTop: 6 }}>
                                <span className="xs">
                                  {order.itemCount} item{order.itemCount === 1 ? '' : 's'}
                                </span>
                                <b className="xs">{money0(order.totals.total)}</b>
                              </div>
                              {can('pos:operate') && order.nextStatuses.length > 0 ? (
                                <div className="row gap-6 wrap" style={{ marginTop: 8 }}>
                                  {order.nextStatuses.map((next) => (
                                    <button
                                      key={next}
                                      className={`btn sm ${next === 'REJECTED' || next === 'CANCELLED' ? '' : 'primary'}`}
                                      disabled={advance.isPending}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        // Delivering raises the invoice. An
                                        // aggregator already took the money;
                                        // for anything else staff say how.
                                        if (
                                          next === 'DELIVERED' &&
                                          !isAggregatorChannel(order.channel)
                                        ) {
                                          setPaying(order);
                                          return;
                                        }
                                        advance.mutate({ id: order.id, status: next });
                                      }}
                                    >
                                      {FULFILMENT_LABELS[next]}
                                    </button>
                                  ))}
                                </div>
                              ) : null}
                            </article>
                          );
                        })
                      )}
                    </div>
                  </section>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {unbilled.length > 0 ? (
        <div className="card">
          <div className="card-head">
            <h2>Delivered but never billed</h2>
            <span className="chip danger">{unbilled.length}</span>
          </div>
          <div className="card-body col gap-16">
            <p className="small">
              These were delivered without an invoice, so they are not in revenue, the stock ledger
              or your GST return. Billing them puts them back where they belong.
            </p>
            <div className="col gap-6">
              {unbilled.map((order) => (
                <div key={order.id} className="row between wrap gap-6">
                  <span className="small">
                    <span className={`chip ${channelTone(order.channel)}`}>
                      {CHANNEL_LABELS[order.channel]}
                    </span>{' '}
                    {order.customerName ?? 'Guest'} · {money0(order.totals.total)}
                  </span>
                  {can('pos:bill') ? (
                    <button
                      className="btn sm primary"
                      disabled={billNow.isPending}
                      onClick={() => billNow.mutate(order.id)}
                    >
                      Bill now
                    </button>
                  ) : null}
                </div>
              ))}
            </div>
            {can('pos:bill') && unbilled.length > 1 ? (
              <button
                className="btn"
                style={{ alignSelf: 'flex-start' }}
                disabled={billNow.isPending}
                onClick={() => {
                  for (const order of unbilled) billNow.mutate(order.id);
                }}
              >
                Bill all {unbilled.length}
              </button>
            ) : null}
          </div>
        </div>
      ) : null}

      {delivered.length + lost.length > 0 ? (
        <div className="card">
          <div className="card-head">
            <h2>Closed today</h2>
            <span className="small muted">{delivered.length + lost.length} orders</span>
          </div>
          <div className="table-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Order</th>
                  <th>Channel</th>
                  <th>Customer</th>
                  <th>Status</th>
                  <th className="num">Bill</th>
                  <th className="num">Commission</th>
                  <th className="num">You keep</th>
                  <th>Invoice</th>
                </tr>
              </thead>
              <tbody>
                {[...delivered, ...lost].map((order) => (
                  <tr key={order.id} className="clickable" onClick={() => setOpenId(order.id)}>
                    <td className="mono xs">{order.externalRef ?? order.number}</td>
                    <td>
                      <span className={`chip ${channelTone(order.channel)}`}>
                        {CHANNEL_LABELS[order.channel]}
                      </span>
                    </td>
                    <td>{order.customerName ?? '—'}</td>
                    <td>
                      <span
                        className={`chip ${order.fulfilment === 'DELIVERED' ? 'ok' : 'danger'}`}
                      >
                        {FULFILMENT_LABELS[order.fulfilment]}
                      </span>
                    </td>
                    <td className="num">{money0(order.totals.total)}</td>
                    <td className="num">
                      {isAggregatorChannel(order.channel) && order.fulfilment === 'DELIVERED'
                        ? money0(order.payout.commission + order.payout.commissionGst)
                        : '—'}
                    </td>
                    <td className="num b">
                      {order.fulfilment === 'DELIVERED' ? money0(order.payout.payout) : '—'}
                    </td>
                    <td className="xs mono">
                      {order.invoiceNumber ? (
                        order.invoiceNumber
                      ) : order.fulfilment === 'DELIVERED' ? (
                        <span className="chip danger">Not billed</span>
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {paying ? (
        <Modal title="How was it paid?" onClose={() => setPaying(null)}>
          <div className="col gap-16">
            <p className="small">
              Delivering <b>{paying.customerName ?? 'this order'}</b> raises the invoice for{' '}
              <b>{money(paying.totals.total)}</b>.
            </p>
            <div className="row gap-6 wrap">
              {(['CASH', 'UPI', 'CARD', 'ONLINE'] as const).map((mode) => (
                <button
                  key={mode}
                  className="btn primary"
                  disabled={advance.isPending}
                  onClick={() => {
                    advance.mutate({ id: paying.id, status: 'DELIVERED', paymentMode: mode });
                    setPaying(null);
                  }}
                >
                  {mode === 'ONLINE' ? 'Paid online' : mode[0] + mode.slice(1).toLowerCase()}
                </button>
              ))}
            </div>
          </div>
        </Modal>
      ) : null}

      {openId ? <OrderDrawer id={openId} onClose={closeDrawer} /> : null}
      {taking ? (
        <TakeOrderModal
          onClose={() => setTaking(false)}
          onDone={() => {
            setTaking(false);
            void queryClient.invalidateQueries({ queryKey: ['online-orders'] });
          }}
        />
      ) : null}
    </div>
  );
}

function OrderDrawer({ id, onClose }: { id: string; onClose: () => void }) {
  const order = useQuery({
    queryKey: ['online-orders', id],
    queryFn: () => api.get<OnlineOrder>(`/api/online-orders/${id}`),
  });
  const data = order.data;

  return (
    <Drawer title={data ? `${CHANNEL_LABELS[data.channel]} order` : 'Order'} onClose={onClose}>
      {order.isLoading || !data ? (
        <Loading />
      ) : (
        <div className="stack">
          <div className="card">
            <div className="card-body col gap-6">
              <div className="row between">
                <b>{data.customerName ?? 'Guest'}</b>
                <span className="chip">{FULFILMENT_LABELS[data.fulfilment]}</span>
              </div>
              <div className="small muted mono">{data.externalRef ?? data.number}</div>
              {data.customerPhone ? <div className="small">{data.customerPhone}</div> : null}
              {data.deliveryAddress ? (
                <div className="small muted">{data.deliveryAddress}</div>
              ) : null}
              {data.tableCode ? <div className="small">Table {data.tableCode}</div> : null}
            </div>
          </div>

          <div className="card">
            <div className="card-head">
              <h2>Items</h2>
            </div>
            <div className="table-wrap">
              <table className="tbl">
                <tbody>
                  {data.lines.map((line) => (
                    <tr key={line.id}>
                      <td>
                        {line.name}
                        {line.note ? <div className="xs muted">{line.note}</div> : null}
                      </td>
                      <td className="num">×{line.quantity}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="card">
            <div className="card-head">
              <h2>What you keep</h2>
              <span className="small muted">{data.commissionPct}% commission</span>
            </div>
            <div className="card-body col gap-6">
              <div className="row between">
                <span>Bill</span>
                <b>{money(data.totals.total)}</b>
              </div>
              <div className="row between">
                <span className="muted">Commission</span>
                <span>− {money(data.payout.commission)}</span>
              </div>
              <div className="row between">
                <span className="muted">GST on commission</span>
                <span>− {money(data.payout.commissionGst)}</span>
              </div>
              <div
                className="row between"
                style={{ borderTop: '1px solid var(--line)', paddingTop: 8 }}
              >
                <b>Payout</b>
                <b>{money(data.payout.payout)}</b>
              </div>
              <p className="small muted">
                An estimate at order time. A real settlement also nets off delivery fees the
                aggregator absorbed, promotions it funded, and TCS/TDS — reconcile against their
                payout statement.
              </p>
            </div>
          </div>

          <div className="card">
            <div className="card-head">
              <h2>Tracking</h2>
            </div>
            <div className="card-body">
              <ol className="track">
                {data.events.map((event) => (
                  <li key={event.id}>
                    <b>{FULFILMENT_LABELS[event.status]}</b>
                    <span className="xs muted">
                      {new Date(event.at).toLocaleString('en-IN', {
                        hour: '2-digit',
                        minute: '2-digit',
                        day: '2-digit',
                        month: 'short',
                      })}
                    </span>
                    {event.note ? <div className="xs muted">{event.note}</div> : null}
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </div>
      )}
    </Drawer>
  );
}

function TakeOrderModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const { push, fail } = useToast();
  const [form, setForm] = useState({
    channel: 'SWIGGY' as OrderChannel,
    externalRef: '',
    customerName: '',
    customerPhone: '',
    deliveryAddress: '',
    commissionPct: '',
  });
  const [lines, setLines] = useState<Record<string, number>>({});
  const [search, setSearch] = useState('');

  const menu = useQuery({
    queryKey: ['menu', 'all'],
    queryFn: () => api.get<MenuRow[]>('/api/menu'),
  });

  const sellable = (menu.data ?? []).filter(
    (item) =>
      item.isActive &&
      item.price > 0 &&
      (search.trim() === '' || item.name.toLowerCase().includes(search.trim().toLowerCase())),
  );

  const chosen = Object.entries(lines).filter(([, qty]) => qty > 0);
  const total = chosen.reduce((sum, [id, qty]) => {
    const item = (menu.data ?? []).find((m) => m.id === id);
    return sum + (item ? item.price * qty : 0);
  }, 0);

  const create = useMutation({
    mutationFn: () =>
      api.post('/api/online-orders', {
        channel: form.channel,
        externalRef: form.externalRef.trim() || undefined,
        customerName: form.customerName.trim(),
        customerPhone: form.customerPhone.trim() || undefined,
        deliveryAddress: form.deliveryAddress.trim() || undefined,
        commissionPct: form.commissionPct === '' ? undefined : Number(form.commissionPct),
        lines: chosen.map(([menuItemId, quantity]) => ({ menuItemId, quantity })),
      }),
    onSuccess: () => {
      push('Order on the board', 'ok');
      onDone();
    },
    onError: fail,
  });

  const ready = form.customerName.trim().length > 0 && chosen.length > 0;

  return (
    <Modal
      title="Take an order"
      onClose={onClose}
      size="wide"
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn primary"
            disabled={!ready || create.isPending}
            onClick={() => create.mutate()}
          >
            {create.isPending ? 'Adding…' : `Add order · ${money0(total)}`}
          </button>
        </>
      }
    >
      <div className="stack">
        <div className="form-grid">
          <Select
            label="Channel"
            value={form.channel}
            onChange={(e) => setForm({ ...form, channel: e.target.value as OrderChannel })}
          >
            {(['SWIGGY', 'ZOMATO', 'PHONE', 'WEB'] as const).map((id) => (
              <option key={id} value={id}>
                {CHANNEL_LABELS[id]}
              </option>
            ))}
          </Select>
          <TextInput
            label="Their order number"
            placeholder="The number on the tablet"
            hint="Keeps a repeat of the same order off the board twice"
            value={form.externalRef}
            onChange={(e) => setForm({ ...form, externalRef: e.target.value })}
          />
          <TextInput
            label="Customer name *"
            value={form.customerName}
            onChange={(e) => setForm({ ...form, customerName: e.target.value })}
          />
          <TextInput
            label="Phone"
            value={form.customerPhone}
            onChange={(e) => setForm({ ...form, customerPhone: e.target.value })}
          />
          <TextInput
            className="span2"
            label="Delivery address"
            value={form.deliveryAddress}
            onChange={(e) => setForm({ ...form, deliveryAddress: e.target.value })}
          />
          {isAggregatorChannel(form.channel) ? (
            <TextInput
              label="Commission %"
              type="number"
              min={0}
              max={100}
              step="0.5"
              hint="Blank uses the rate set for this channel"
              value={form.commissionPct}
              onChange={(e) => setForm({ ...form, commissionPct: e.target.value })}
            />
          ) : null}
        </div>

        <div className="card">
          <div className="card-head">
            <h3>Items</h3>
            <div className="search" style={{ width: 200 }}>
              <input
                className="input"
                placeholder="Search the menu"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>
          <div className="card-body">
            {menu.isLoading ? (
              <Loading />
            ) : sellable.length === 0 ? (
              <Empty message="Nothing on sale to order — price an item first." />
            ) : (
              <div className="pick-list">
                {sellable.slice(0, 60).map((item) => (
                  <div key={item.id} className="pick-row">
                    <div>
                      <div className="b">{item.name}</div>
                      <div className="xs muted">
                        {item.category} · {money0(item.price)}
                      </div>
                    </div>
                    <div className="row gap-6">
                      <button
                        className="btn ghost icon sm"
                        onClick={() =>
                          setLines((l) => ({ ...l, [item.id]: Math.max(0, (l[item.id] ?? 0) - 1) }))
                        }
                      >
                        −
                      </button>
                      <span className="mono" style={{ minWidth: 20, textAlign: 'center' }}>
                        {lines[item.id] ?? 0}
                      </span>
                      <button
                        className="btn ghost icon sm"
                        onClick={() =>
                          setLines((l) => ({ ...l, [item.id]: (l[item.id] ?? 0) + 1 }))
                        }
                      >
                        +
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
}
